import { Button } from '@/components/ui/button';
import { GradientDialog } from '@/components/ui/gradient-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import { Mail } from 'lucide-react';

interface EmailConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email?: string;
}

export function EmailConfirmationModal({
  open,
  onOpenChange,
  email,
}: EmailConfirmationModalProps) {
  return (
    <AnimatePresence>
      {open && (
        <GradientDialog
          open={open}
          onOpenChange={onOpenChange}
          gradientFrom="from-purple-500/10"
          gradientTo="to-blue-500/5"
          borderColor="border-purple-500/20"
          contentClassName="flex flex-col items-center justify-center"
          maxWidth="sm"
        >
          <div className="flex flex-col items-center justify-center py-6 space-y-6">
            {/* Envelope animation */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20,
              }}
              className="relative w-24 h-24 mb-2"
            >
              <div className="absolute inset-0 bg-linear-to-br from-purple-400 to-blue-500 rounded-xl opacity-20 blur-xl" />
              <div className="relative flex items-center justify-center w-full h-full bg-linear-to-br from-purple-500/30 to-blue-500/30 backdrop-blur-xs rounded-xl border border-white/20">
                <Mail className="w-12 h-12 text-white" />
              </div>
            </motion.div>

            {/* Title with animation */}
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-xl font-semibold text-center"
            >
              Check your email
            </motion.h2>

            {/* Message with animation */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="text-center space-y-2"
            >
              <p className="text-muted-foreground">
                We've sent a confirmation link to:
              </p>
              <p className="font-medium text-primary">
                {email || 'your email address'}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Please check your inbox and click the link to activate your
                account.
              </p>
            </motion.div>

            {/* Animated button */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="w-full mt-4"
            >
              <Button
                onClick={() => onOpenChange(false)}
                className="w-full relative overflow-hidden group"
              >
                <span className="relative z-10">Got it</span>
                <motion.div
                  className="absolute inset-0 bg-linear-to-r from-purple-500 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  initial={false}
                  animate={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                />
              </Button>
            </motion.div>

            {/* Subtle hint */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="text-xs text-muted-foreground mt-4"
            >
              Didn't receive an email? Check your spam folder.
            </motion.p>
          </div>
        </GradientDialog>
      )}
    </AnimatePresence>
  );
}
